import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import {
    IFormUUID,
    IReviewsProductTypeID,
    isoFormUUID,
    isoReviewsBrandID,
} from "../../../models/nominals";
import { ReviewSortOption } from "../../../models/reviews";
import { UGCListSubBlock } from "../../../models/ugc";
import { useSSRStorePrefill } from "../../../store/reducers";
import { ProductReviews } from "../ProductReviews";
import { ReviewsComponentTypes } from "../constants";
import { prefillSSRStoreData } from "../ssr";

type IProps = Immutable<
    templates.ProductReviewsTemplate & {
        // Site-specific overrides (not in Django template context)
        brand?: string;
        starHasStroke?: boolean;
        showWriteReviewButton?: boolean;
        reviewsComponentType?: ReviewsComponentTypes;
        defaultSortOption?: ReviewSortOption;
        pageSize?: number;
        displayedProductTypeIDWhitelist?: readonly IReviewsProductTypeID[];
        sourceFilterWhitelist?: readonly string[];
        isCollapsedView?: boolean;
    }
>;

export const ProductReviewsSSR = (props: IProps): React.JSX.Element => {
    useSSRStorePrefill(
        {
            products_data: props.products_data,
            reviews_data: props.reviews_data,
            facets_data: props.facets_data,
        },
        prefillSSRStoreData,
    );

    const brandID = isoReviewsBrandID.wrap(props.reviews_brand_id ?? 0);
    // useId() produces stable IDs that match between SSR and client hydration,
    // unlike uuidv4() which generates a different value on each render.
    const writeReviewFormUUID: IFormUUID = isoFormUUID.wrap(React.useId());

    // Parse UGC data if present
    const { ugcCarouselDesc, ugcCarouselPosts } = React.useMemo(() => {
        if (props.ugc_data) {
            const ugcData = JSON.parse(props.ugc_data);
            return {
                ugcCarouselDesc: ugcData.desc as string | undefined,
                ugcCarouselPosts: ugcData.posts as
                    | UGCListSubBlock[]
                    | undefined,
            };
        }
        return { ugcCarouselDesc: undefined, ugcCarouselPosts: undefined };
    }, [props.ugc_data]);

    return (
        <ProductReviews
            title={props.title}
            brandID={brandID}
            numReviews={props.num_reviews}
            averageRating={props.average_rating}
            showProductsFilter={props.show_products_filter}
            showSourcesFilter={props.show_sources_filter}
            showWriteReviewButton={props.showWriteReviewButton}
            writeReviewFormUUID={writeReviewFormUUID}
            ugcCarouselDesc={ugcCarouselDesc}
            ugcCarouselPosts={ugcCarouselPosts}
            brand={props.brand}
            starHasStroke={props.starHasStroke}
            reviewsComponentType={props.reviewsComponentType}
            defaultSortOption={props.defaultSortOption}
            pageSize={props.pageSize}
            displayedProductTypeIDWhitelist={
                props.displayedProductTypeIDWhitelist
            }
            sourceFilterWhitelist={props.sourceFilterWhitelist}
            isCollapsedView={props.isCollapsedView}
        />
    );
};
